import { useCallback } from "react";
import { FormikHelpers } from "formik";
import plur from "plur";
import { toast } from "@jsq/ds/core";
import { JsqAccessTypeEnum } from "~/graphql/supergraphEnums";
import { useCreateArenaAccessMutation } from "~/graphql/supergraphHooks";
import { toGlobalId } from "~/utils/graphql-helpers";

export type FormValues = {
  arenaId: string;
  roleGroupName: string;
  accessType: JsqAccessTypeEnum | "";
  expirationDate: Date | null;
};

export type AssignToWorkspaceModalProps = {
  userIds: string[] | number[];
  onClose: () => void;
};

export const useAssignToWorkspaceModal = ({
  userIds,
  onClose,
}: AssignToWorkspaceModalProps) => {
  const [createArenaAccess, { loading }] = useCreateArenaAccessMutation();

  const successMessage = `${plur("User", userIds?.length ?? 0)} assigned to workspace`;
  const errorMessage = `Failed to assign ${plur("user", userIds?.length ?? 0)} to workspace`;

  const initialValues: FormValues = {
    arenaId: "",
    roleGroupName: "",
    accessType: "",
    expirationDate: null,
  };

  const createWorkspaceAccess = useCallback(
    async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
      if (!values.accessType) {
        toast.error("Access type is required");
        return;
      }

      const globalArenaId = toGlobalId("ArenaObjectType", values.arenaId);
      const globalUserId = userIds.map((id) => toGlobalId("UserObjectType", id));

      try {
        const response = await createArenaAccess({
          fetchPolicy: "network-only",
          variables: {
            input: {
              arenaId: globalArenaId,
              userIds: globalUserId,
              accessType: values.accessType,
              roleGroupName: values.roleGroupName,
              endDate: values.expirationDate ? values.expirationDate.toISOString() : null,
            },
          },
          refetchQueries: ["ArenaAccessForUser", "ActiveArenaAccesses"],
        });
        const data = response.data?.createArenaAccess;

        if (data?.__typename === "CreateArenaAccessResponse") {
          toast.success(successMessage);
          resetForm();
          onClose();
        } else if (data?.__typename === "MutationErrorResponse") {
          toast.error(data.message);
        } else {
          toast.error("There was an error with the operation");
        }
      } catch {
        toast.error(errorMessage);
      }
    },

    [createArenaAccess, userIds, onClose],
  );

  return {
    initialValues,
    createWorkspaceAccess,
    loading,
  };
};
